// src/components/inventory/InventoryProductsTab.tsx
// Elenco dei prodotti; la scheda del singolo prodotto si apre in un modale,
// da lì la merce esce (usata in servizio o venduta) e si modifica o elimina.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Search } from 'lucide-react';

import InventoryProductDetailModal from '@/components/inventory/InventoryProductDetailModal';
import InventoryProductFormModal from '@/components/inventory/InventoryProductFormModal';
import { useInventory } from '@/contexts/InventoryContext';

interface InventoryProductsTabProps {
    focusProductId?: number | null;
    onFocusHandled?: () => void;
}

const InventoryProductsTab: React.FC<InventoryProductsTabProps> = ({
    focusProductId,
    onFocusHandled,
}) => {
    const { t } = useTranslation();
    const { products } = useInventory();

    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);

    useEffect(() => {
        if (focusProductId) {
            setSelectedId(focusProductId);
            setIsDetailOpen(true);
            onFocusHandled?.();
        }
    }, [focusProductId, onFocusHandled]);

    const selected = useMemo(
        () => products.find((p) => p.id === selectedId) || null,
        [products, selectedId]
    );

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return products;
        return products.filter(
            (p) =>
                p.name.toLowerCase().includes(term) ||
                p.category.toLowerCase().includes(term) ||
                (p.barcode || '').includes(term)
        );
    }, [products, search]);

    const totalValue = useMemo(
        () => products.reduce((sum, p) => sum + Number(p.price) * p.stock, 0),
        [products]
    );

    const openProduct = (id: number) => {
        setSelectedId(id);
        setIsDetailOpen(true);
    };

    return (
        <div className="space-y-3">
            <p className="text-sm text-gray-500">
                {t('inventory.summary', '{{count}} prodotti · valore €{{value}}', {
                    count: products.length,
                    value: totalValue.toFixed(2),
                })}
            </p>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('inventory.search_placeholder', 'Cerca prodotto...') as string}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none shadow-sm placeholder:text-gray-400"
                />
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                {filtered.length === 0 ? (
                    <p className="p-8 text-center text-sm text-gray-400">
                        {t('inventory.empty', 'Nessun prodotto: scansiona un codice per farne entrare uno.')}
                    </p>
                ) : (
                    filtered.map((p, index) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => openProduct(p.id)}
                            className={`w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors ${index > 0 ? 'border-t border-gray-100' : ''
                                }`}
                        >
                            <div className="min-w-0 flex-1">
                                <p className="font-bold text-gray-900 truncate">{p.name}</p>
                                <p className="text-xs text-gray-500">
                                    {p.category} · €{Number(p.price).toFixed(2)}
                                </p>
                            </div>
                            <span
                                className={`flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-bold ${p.stock <= p.min_threshold
                                    ? 'bg-red-50 text-red-600'
                                    : 'bg-emerald-50 text-emerald-700'
                                    }`}
                            >
                                {p.stock} {t('inventory.pieces_short', 'pz')}
                            </span>
                            <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                        </button>
                    ))
                )}
            </div>

            <InventoryProductDetailModal
                isOpen={isDetailOpen}
                onClose={() => setIsDetailOpen(false)}
                product={selected}
                onEdit={() => setIsEditOpen(true)}
            />

            <InventoryProductFormModal
                isOpen={isEditOpen}
                onClose={() => setIsEditOpen(false)}
                product={selected}
            />
        </div>
    );
};

export default InventoryProductsTab;
